import { useState, useEffect, useCallback } from 'react';
import { contactService } from '../services/api';

export function useContacts({ enabled = true } = {}) {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(enabled); // Only load if enabled
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1, limit: 50, total: 0, totalPages: 1
    });

    // Fetch contacts with pagination params
    const fetchContacts = useCallback(async (paramsObj = {}) => {
        setLoading(true);
        setError(null);
        try {
            const result = await contactService.getAll(paramsObj);

            setContacts(result.data);
            setPagination(result.pagination);
            return result.data;
        } catch (err) {
            setError(err.message);
            console.error('Failed to fetch contacts:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Search contacts
    const searchContacts = useCallback(async (query) => {
        return await fetchContacts({ search: query, page: 1, limit: 50 });
    }, [fetchContacts]);

    useEffect(() => {
        if (enabled) {
            fetchContacts({ page: 1, limit: 50 });
        } else {
            setLoading(false);
        }
    }, [fetchContacts, enabled]);

    const addContact = useCallback(async (contactData) => {
        try {
            const newContact = await contactService.create(contactData);
            setContacts(prev => [newContact, ...prev]);
            setPagination(prev => ({ ...prev, total: prev.total + 1 }));
            return newContact;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const updateContact = useCallback(async (id, contactData) => {
        try {
            const updated = await contactService.update(id, contactData);
            setContacts(prev => prev.map(c => c.id === id ? updated : c));
            return updated;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteContact = useCallback(async (id) => {
        try {
            await contactService.delete(id);
            setContacts(prev => prev.filter(c => c.id !== id));
            setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteAllContacts = useCallback(async () => {
        try {
            await contactService.deleteAll();
            setContacts([]);
            setPagination(prev => ({ ...prev, total: 0, page: 1, totalPages: 1 }));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    return {
        contacts,
        loading,
        error,
        pagination,
        fetchContacts,
        searchContacts,
        addContact,
        updateContact,
        deleteContact,
        deleteAllContacts
    };
}
